/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, December 9th 2024, 10:30:01 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import ChooseIcon from "@mui/icons-material/List";
import CloseIcon from "@mui/icons-material/Close";
import Stack from "@mui/material/Stack";
import ToolTip from "@mui/material/Tooltip";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import ListItemButton from "@mui/material/ListItemButton";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import { withAccessCheck } from "authorization/hoc";
import { useGlobalScope } from "core/providers";
import { getDefaultIntegrationConnection } from "lib/factories";
import { IntegrationProvider, IntegrationConnection, TaskTemplate } from "lib/schemas";
import { useIntegrationsAPI } from "integrations/services/IntegrationsAPI";
import { useIntegrationStore, getIntegrationConnection } from "integrations/stores";
import {
  APIListProgressWrapper,
  ActionDialog,
  OSAvatar,
  OSActionButton,
  StyledOSDialog,
  PopupMenu,
  Image,
  IconText,
  useToggleState,
  EmptyStateActionWrapper,
  EmphasisText,
} from "ui-widgets";
import { TextFormInput, CheckBoxMultipleFormInput, OSAPIForm } from "ui-widgets/forms";
import { DeleteOSItemDialog } from "version-control/components/DeleteOSItemDialog";
import { CredentialStoreInput } from "./CredentialStoreInput";

interface ISelectedIntegration {
  provider: IntegrationProvider;
  integration_actions: IntegrationProvider["actions"][number]["code"][];
  integrationConnectionId: IntegrationConnection["id"];
}

interface IntegrationConnectionsListProps {
  onAddExistingConnection?: (connection: IntegrationConnection) => void;
  onDetachConnection?: (connectionId: IntegrationConnection["id"]) => void;
  onDeleteConnection: (connectionId: IntegrationConnection["id"]) => void;
  onSaveIntegrationConnection: (connection: IntegrationConnection) => void;
  integrationConnections?: IntegrationConnection[];
  integrationProviders?: IntegrationProvider[];
  taskTemplateTitle?: TaskTemplate["title"];
  taskTemplateIds?: TaskTemplate["id"][];
}

export const IntegrationConnectionsList = withAccessCheck(
  { resourceId: "configureIntegrations" },
  ({
    taskTemplateTitle,
    taskTemplateIds = [],
    integrationConnections,
    onAddExistingConnection,
    onDetachConnection,
    onDeleteConnection,
    integrationProviders,
    onSaveIntegrationConnection,
  }: IntegrationConnectionsListProps) => {
    const getIntegrationProvider = React.useCallback(
      (integrationCode: string) => {
        return integrationProviders?.find((provider) => provider.code === integrationCode);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [integrationProviders.length]
    );

    const [selectedIntegration, setSelectedIntegration] = React.useState<ISelectedIntegration>();

    const { removeIntegrationConnection } = useIntegrationStore();

    const onSelectIntegrationAction: React.ComponentProps<typeof NewIntegrationButton>["onSelectIntegrationAction"] = ({
      integration_actions,
      provider,
      integrationConnectionId,
    }) => {
      setSelectedIntegration({ integration_actions, provider, integrationConnectionId });
      handleOpenConnectionForm();
    };

    const onClickEditConnection = (connection: IntegrationConnection) => {
      const provider = getIntegrationProvider(connection.integration_code);
      setSelectedIntegration({
        integration_actions: connection.integration_actions,
        provider,
        integrationConnectionId: connection.id,
      });
      handleOpenConnectionForm();
    };

    const { useDeleteIntegrationConnection } = useIntegrationsAPI();
    const newIntegrationButton = (
      <NewIntegrationButton
        integrationProviders={integrationProviders}
        onSelectIntegrationAction={onSelectIntegrationAction}
      />
    );
    const existingIntegrationButton = onAddExistingConnection ? (
      <ExistingIntegrationButton onAddExistingConnection={onAddExistingConnection} />
    ) : null;

    const {
      handleOpen: handleOpenConnectionForm,
      handleClose: handleCloseConnectionForm,
      open: connectionFormIsOpen,
    } = useToggleState();

    const {
      globalState: { selectedOrganization },
    } = useGlobalScope();

    return (
      <>
        <Stack direction="column" spacing={2}>
          {integrationProviders?.length ? (
            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              {newIntegrationButton}
              {existingIntegrationButton}
            </Stack>
          ) : null}{" "}
          {integrationConnections?.map((integrationConnection) => {
            const provider = getIntegrationProvider(integrationConnection.integration_code);
            return (
              <ListItemButton key={`${integrationConnection.id}`}>
                <Grid container>
                  <Grid item md={2}>
                    <Image src={provider?.icon} height={25} />
                  </Grid>
                  <Grid item md={5}>
                    <ListItemText primary={integrationConnection.name} />
                  </Grid>
                  <Grid item md={3}>
                    <ListItemText primary={`${integrationConnection.integration_actions?.length ?? 0} action(s)`} />
                  </Grid>
                  <Grid item md={1}>
                    <Stack direction="row">
                      <Tooltip title={<Typography>Configuration</Typography>}>
                        <IconButton onClick={() => onClickEditConnection(integrationConnection)}>
                          <SettingsIcon color="primary" />
                        </IconButton>
                      </Tooltip>
                      {onDetachConnection ? (
                        <Tooltip title={<Typography>Detach Connection</Typography>}>
                          <IconButton
                            edge="end"
                            aria-label="remove"
                            onClick={() => onDetachConnection(integrationConnection.id)}
                          >
                            <CloseIcon color="error" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      <DeleteOSItemDialog<IntegrationConnection>
                        itemId={integrationConnection.id}
                        onDelete={() => {
                          onDeleteConnection(integrationConnection.id);
                          removeIntegrationConnection(integrationConnection.id);
                        }}
                        onRestore={() => {}}
                        useDeleteOSItem={useDeleteIntegrationConnection}
                      >
                        {({ handleOpen }) => (
                          <Tooltip title={<Typography>Delete Connection</Typography>}>
                            <IconButton edge="end" aria-label="remove" onClick={handleOpen}>
                              <DeleteIcon color="error" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </DeleteOSItemDialog>
                    </Stack>
                  </Grid>
                </Grid>
              </ListItemButton>
            );
          })}
          {integrationConnections?.length ? null : (
            <div>
              <IntegrationsEmptyState taskTemplateTitle={taskTemplateTitle} actions={newIntegrationButton} />
            </div>
          )}
        </Stack>
        {selectedIntegration ? (
          <IntegrationConnectionForm
            onSaveIntegrationConnection={onSaveIntegrationConnection}
            open={connectionFormIsOpen}
            onClose={handleCloseConnectionForm}
            integrationConnectionDefaultValues={getDefaultIntegrationConnection({
              defaults: {
                name: `${selectedIntegration.provider.name} integration${
                  taskTemplateTitle ? ` for task named '${taskTemplateTitle}'` : ""
                }`,
                integration_actions: selectedIntegration.integration_actions,
                integration_task_templates: taskTemplateIds,
              },
              organization: selectedOrganization,
              provider: selectedIntegration.provider,
            })}
            selectedIntegration={selectedIntegration}
          />
        ) : null}
      </>
    );
  }
);

interface IntegrationConnectionFormProps extends Pick<React.ComponentProps<typeof StyledOSDialog>, "open" | "onClose"> {
  selectedIntegration: ISelectedIntegration;
  integrationConnectionId?: IntegrationConnection["id"];
  onSaveIntegrationConnection: (connection: IntegrationConnection) => void;
  integrationConnectionDefaultValues?: IntegrationConnection;
}

export const IntegrationConnectionForm = ({
  open,
  onClose,
  integrationConnectionDefaultValues,
  selectedIntegration: { provider, integration_actions, integrationConnectionId },
  onSaveIntegrationConnection,
}: IntegrationConnectionFormProps) => {
  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();

  const { useIntegrationConnectionAPIForm } = useIntegrationsAPI();

  const { addIntegrationConnection } = useIntegrationStore();

  const integrationConnection = useIntegrationStore(getIntegrationConnection(integrationConnectionId));

  const useAPIFormReturn = useIntegrationConnectionAPIForm({
    integrationConnectionDefaultValues: integrationConnection ?? integrationConnectionDefaultValues,
    integrationConnectionFetchId: null,
    updateIntegrationConnectionFromAPICallback: (connection) => {
      addIntegrationConnection(connection);
      onSaveIntegrationConnection?.(connection);
      onClose(null, "backdropClick");
    },
  });

  return (
    <StyledOSDialog
      open={open}
      onClose={onClose}
      popUpTitle={`${provider.name} integration`}
      popUpIcon={<Image src={provider.icon} height={50} />}
    >
      <OSAPIForm useAPIFormReturn={useAPIFormReturn}>
        <Stack sx={{ marginBottom: 4 }}>
          <IconText
            icon={<Image src="/img/actionable.png" height={40} />}
            textComponent={<Typography variant="h6">Actions</Typography>}
          />
          <CheckBoxMultipleFormInput<IntegrationConnection, string>
            schema_field_key="integration_actions"
            options={provider.actions.map((v) => v.code)}
            getOptionLabel={(code) => {
              const action = provider.actions.find((v) => v.code === code);
              return <ListItemText primary={action.name} secondary={action.description} />;
            }}
            defaultValue={integration_actions}
          />
        </Stack>
        <TextFormInput<IntegrationConnection> schema_field_key="name" label="Description" fullWidth />
        <CredentialStoreInput<IntegrationConnection>
          schema_field_key="credentials_store"
          newCredentialStoreName={`${provider.name} credentials for ${selectedOrganization.name}`}
        />
      </OSAPIForm>
    </StyledOSDialog>
  );
};

interface NewIntegrationButtonProps {
  onSelectIntegrationAction: (args: ISelectedIntegration) => void;
  integrationProviders: IntegrationProvider[];
}

type PopupMenuItems = React.ComponentProps<typeof PopupMenu>["menuItems"];

const NewIntegrationButton = ({ onSelectIntegrationAction, integrationProviders }: NewIntegrationButtonProps) => {
  const menutItems: PopupMenuItems = integrationProviders.map((provider) => ({
    icon: null,
    text: provider.name,
    onClick: null,
    renderMenuItem: () => (
      <IntegrationActionsMenu
        integrationProvider={provider}
        onSelectIntegrationAction={onSelectIntegrationAction}
        renderOpenButton={({ handleOpen }) => (
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ width: "100px" }}
            onMouseOver={(e: any) => handleOpen(e)}
          >
            <ToolTip title={provider.name} placement="right-end">
              <div>
                <Image src={provider.icon} width={60} />
              </div>
            </ToolTip>
          </Stack>
        )}
      />
    ),
  }));

  return (
    <PopupMenu
      menuItems={menutItems}
      renderOpenButton={({ handleOpen }) => (
        <OSActionButton
          onClick={(event) => {
            handleOpen(event);
          }}
          actionStyle="add"
          variant="contained"
        >
          New Integration
        </OSActionButton>
      )}
    />
  );
};

interface IntegrationActionsMenuProps
  extends Pick<React.ComponentProps<typeof PopupMenu>, "renderOpenButton">,
    Pick<NewIntegrationButtonProps, "onSelectIntegrationAction"> {
  integrationProvider: IntegrationProvider;
}

const IntegrationActionsMenu = ({
  integrationProvider,
  renderOpenButton,
  onSelectIntegrationAction,
}: IntegrationActionsMenuProps) => {
  const menuItems: PopupMenuItems = integrationProvider.actions.map((action) => ({
    icon: null,
    text: action.name,
    onClick: () =>
      onSelectIntegrationAction({
        integration_actions: [action.code],
        integrationConnectionId: null,
        provider: integrationProvider,
      }),
  }));
  return (
    <PopupMenu
      menuItems={menuItems}
      renderOpenButton={renderOpenButton}
      anchorOrigin={{ vertical: -10, horizontal: 100 }}
    />
  );
};

interface IntegrationsEmptyStateProps {
  actions: React.ReactNode;
  taskTemplateTitle?: TaskTemplate["title"];
}

const IntegrationsEmptyState = ({ actions, taskTemplateTitle = null }: IntegrationsEmptyStateProps) => {
  return (
    <EmptyStateActionWrapper
      title={
        taskTemplateTitle ? (
          <>
            No integrations are currently configured for <EmphasisText>{taskTemplateTitle}</EmphasisText>
          </>
        ) : (
          <>No integrations are currently configured</>
        )
      }
      description={
        <>
          Add an integration to trigger actions in external systems and/or custom modules
          {taskTemplateTitle ? (
            <>
              {" "}
              when the '<EmphasisText>{taskTemplateTitle}</EmphasisText>' task is completed
            </>
          ) : null}
          .
        </>
      }
      illustrationGraphic={<Image className="image-style-A" src="/img/integration.png" alt="Onestop Admin" />}
    >
      {actions}
    </EmptyStateActionWrapper>
  );
};

interface ExistingIntegrationButtonProps
  extends Required<Pick<IntegrationConnectionsListProps, "onAddExistingConnection">> {}

const ExistingIntegrationButton = ({ onAddExistingConnection }: ExistingIntegrationButtonProps) => {
  const { useAPIListIntegrationConnections } = useIntegrationsAPI();
  const { fetchConnections, integrationConnections, isMakingAPIRequest, isAPIError, httpResponseStatusCode } =
    useAPIListIntegrationConnections();
  const loadExistingIntegrations = () => {
    fetchConnections({ loadAll: true });
  };

  return (
    <StyledOSDialog
      popUpTitle="Choose Existing Integration"
      popUpIcon={<ChooseIcon color="secondary" />}
      renderOpenButton={({ handleOpen }) => (
        <OSActionButton
          onClick={() => {
            handleOpen();
            loadExistingIntegrations();
          }}
          actionStyle="custom"
          variant="contained"
          customIcon={<ChooseIcon />}
        >
          Choose Existing Integration
        </OSActionButton>
      )}
      renderContentWithClose={({ handleClose }) => (
        <APIListProgressWrapper
          showLoader={isMakingAPIRequest}
          loaderType="circularCentered"
          isAPIError={isAPIError}
          items={integrationConnections}
          httpResponseStatusCode={httpResponseStatusCode}
        >
          {integrationConnections.map((connection) => (
            <ExistingIntegrationListItem
              key={connection.id}
              integrationConnection={connection}
              onAddExistingConnection={(conn) => {
                onAddExistingConnection(conn);
                handleClose();
              }}
            />
          ))}
        </APIListProgressWrapper>
      )}
    />
  );
};

const ExistingIntegrationListItem = ({
  integrationConnection,
  onAddExistingConnection,
}: {
  integrationConnection: IntegrationConnection;
  onAddExistingConnection: ExistingIntegrationButtonProps["onAddExistingConnection"];
}) => {
  const {
    handleClose: handleCloseConfirmation,
    handleOpen: handleOpenConfirmation,
    open: confirmationOpened,
  } = useToggleState();

  return (
    <>
      <ListItemButton onClick={handleOpenConfirmation}>
        <Grid container>
          <Grid item md={1}>
            <OSAvatar name={integrationConnection.name} variant="rounded" sx={{ width: 28, height: 28 }} />
          </Grid>
          <Grid item md={11}>
            <ListItemText primary={integrationConnection.name} />
          </Grid>
        </Grid>
      </ListItemButton>
      <ActionDialog
        title="Confirm Choice"
        severity="info"
        open={confirmationOpened}
        onClose={handleCloseConfirmation}
        confirmationAction={() => {
          onAddExistingConnection(integrationConnection);
          handleCloseConfirmation();
        }}
        dialogMessage={
          <>
            Are you sure you want to choose <EmphasisText>{integrationConnection.name}</EmphasisText>?
          </>
        }
      />
    </>
  );
};
